// skipcq: JS-W1028, JS-0128
import React, { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../utils/firebase';
// Material UI
import {
  Box,
  Paper,
  CardContent,
  CardActions,
  Button,
  Alert,
  CircularProgress,
  Typography,
  TextField,
  Grid,
} from '@mui/material';
import DashboardSurfaceCard from './DashboardSurfaceCard';

function HaikuCard() {
  // Haiku functionality state
  const [haikuLoading, setHaikuLoading] = useState(false);
  const [haiku, setHaiku] = useState(null);
  const [haikuError, setHaikuError] = useState(null);

  // Input state for customization
  const [theme, setTheme] = useState('Idols');
  const [beerStyle, setBeerStyle] = useState('Imperial Stout');

  // Handle closing error alert
  const handleCloseError = useCallback(() => {
    setHaikuError(null);
  }, []);

  // Handle input changes
  const handleThemeChange = useCallback((e) => {
    setTheme(e.target.value);
  }, []);

  const handleBeerStyleChange = useCallback((e) => {
    setBeerStyle(e.target.value);
  }, []);

  const generateHaiku = useCallback(async () => {
    setHaikuLoading(true);
    setHaikuError(null);
    setHaiku(null);

    try {
      const haikuFlow = httpsCallable(
        functions,
        'genkitGenerateBeerHaiku-generateBeerHaiku',
      );
      const response = await haikuFlow({
        theme,
        beer_style: beerStyle,
      });
      setHaiku(response.data);
    } catch (error) {
      console.error('Error generating haiku:', error);
      const errorMessage =
        error.message?.includes('CORS') ||
        error.message?.includes('Access-Control')
          ? 'Service temporarily unavailable. CORS configuration needed on backend.'
          : 'Failed to generate haiku. Please try again.';
      setHaikuError(errorMessage);
    } finally {
      setHaikuLoading(false);
    }
  }, [theme, beerStyle]);

  return (
    <DashboardSurfaceCard>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Beer Haiku Generator
        </Typography>
        <Typography variant="body2" paragraph sx={{
          color: "text.secondary"
        }}>
          Generate custom beer haikus with AI
        </Typography>

        {/* Input fields for customization */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Theme"
              value={theme}
              onChange={handleThemeChange}
              size="small"
              disabled={haikuLoading}
              placeholder="e.g., Idols, Friday night, Summer..."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Beer Style"
              value={beerStyle}
              onChange={handleBeerStyleChange}
              size="small"
              disabled={haikuLoading}
              placeholder="e.g., Imperial Stout, IPA..."
            />
          </Grid>
        </Grid>

        {/* Haiku loading indicator */}
        {haikuLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2
            }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Crafting your beer haiku...
            </Typography>
          </Box>
        )}

        {/* Display generated haiku */}
        {haiku && !haikuLoading && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              textAlign: 'center',
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'rgba(90, 80, 69, 0.06)',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Your Beer Haiku:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-line',
                fontStyle: 'italic',
                lineHeight: 1.6,
              }}
            >
              {haiku}
            </Typography>
          </Paper>
        )}

        {/* Display error message */}
        {haikuError && !haikuLoading && (
          <Alert severity="error" onClose={handleCloseError} sx={{ mb: 2 }}>
            {haikuError}
          </Alert>
        )}
      </CardContent>
      <CardActions>
        <Button
          variant="contained"
          onClick={generateHaiku}
          disabled={haikuLoading}
          fullWidth
        >
          {haikuLoading ? 'Generating...' : 'Generate Haiku'}
        </Button>
      </CardActions>
    </DashboardSurfaceCard>
  );
}

export default HaikuCard;
